
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { OptionCard } from "./OptionCard";
import { audiences, privacyOptions, monetizationOptions, responseTypeOptions } from "./constants";
import type { PositType, PositAudience, PositPrivacy, PositResponseType, OnboardingFormData } from "./types";

interface OnboardingFlowProps {
  onPositCreated: (positId: string) => void;
}

const OnboardingFlow = ({ onPositCreated }: OnboardingFlowProps) => {
  const [formData, setFormData] = useState<OnboardingFormData>({
    positName: "",
    publicDescription: "",
    audience: "public",
    privacy: "public",
    monetization: "free",
    price: 5,
    responseType: "you"
  });
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check user subscription status
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user?.id) return;

      try {
        const { data: userData } = await supabase
          .from("users")
          .select("subscription_type, trial_end_date")
          .eq('id', user.id)
          .single();

        const isTrialValid = userData?.trial_end_date 
          ? new Date(userData.trial_end_date) > new Date() 
          : false;

        const isPremiumOrTrial = userData?.subscription_type === 'posit_plus' || 
          (userData?.subscription_type === 'trial' && isTrialValid);

        setIsPremium(isPremiumOrTrial);
      } catch (error) {
        setIsPremium(false);
      }
    };

    checkSubscriptionStatus();
  }, [user?.id]);

  const handleInputChange = (field: keyof OnboardingFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePrivacySelect = (value: string) => {
    // Prevent selection of private/exclusive for non-premium users
    if (!isPremium && (value === 'private' || value === 'exclusive')) {
      toast({
        title: "Premium Required",
        description: "Upgrade to Posit Plus to use private and exclusive privacy settings",
        variant: "destructive"
      });
      return;
    }
    handleInputChange("privacy", value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "User not authenticated"
      });
      return;
    }

    // Validation
    if (!formData.positName.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a name for your Posit"
      });
      return;
    }

    if (!formData.audience || !formData.privacy || !formData.monetization) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please make all selections"
      });
      return;
    }

    // Validate price for paid posits
    if (formData.monetization === "paid" && (!formData.price || formData.price < 1)) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please set a valid monthly price (minimum $1)"
      });
      return;
    }

    // Double-check premium status for private/exclusive posits
    if ((formData.privacy === "private" || formData.privacy === "exclusive") && !isPremium) {
      toast({
        variant: "destructive",
        title: "Premium Feature",
        description: "Private and exclusive posits require a Posit Plus subscription"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from("posits")
        .insert({
          user_id: user.id,
          title: formData.positName.trim(),
          public_description: formData.publicDescription.trim() || null,
          type: "user_knowledge_base" as PositType, // Always second brain
          audience: formData.audience as PositAudience,
          privacy: formData.privacy as PositPrivacy,
           response_type: "you" as PositResponseType,
          is_monetized: formData.monetization === "paid",
          is_paid: formData.monetization === "paid",
          subscription_price: formData.monetization === "paid" ? formData.price : null,
        })
        .select()
        .single();

      if (error) throw error;

      // Create welcome message
      const { error: messageError } = await supabase
        .from("chat_messages")
        .insert({
          posit_id: data.id,
          role: "assistant",
          content: "Welcome! I'm here to help you document your knowledge. What would you like to share?",
          created_by: user.id
        });

      if (messageError) throw messageError;

      onPositCreated(data.id);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create Posit. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 p-4 lg:p-6 space-y-6">
      <div className="w-full max-w-3xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="posit-name" className="text-base font-medium">
              Name
            </Label>
            <Input
              id="posit-name"
              value={formData.positName}
              onChange={(e) => handleInputChange("positName", e.target.value)}
              className="text-lg h-12"
              maxLength={100}
              autoFocus
            />
            <p className="text-sm text-muted-foreground">
              e.g., Marketing Strategy Guide, Python Coding Help, Recipe Collection
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="public-description" className="text-base font-medium">
              Description <span className="text-sm text-muted-foreground">(Optional)</span>
            </Label>
            <Textarea
              id="public-description"
              placeholder=""
              value={formData.publicDescription}
              onChange={(e) => handleInputChange("publicDescription", e.target.value)}
              className="resize-none min-h-[60px]"
              maxLength={80}
            />
            <div className="text-xs text-muted-foreground text-right">
              {formData.publicDescription.length}/80 characters
            </div>
          </div>

          {/* Who is this Posit for? */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Who is this Posit for?</Label>
            <div className="grid grid-cols-3 gap-4">
              {audiences.map((option) => (
                <OptionCard
                  key={option.value}
                  option={option}
                  isSelected={formData.audience === option.value}
                  onSelect={(value) => handleInputChange("audience", value)}
                  isHovered={hoveredCard === option.value}
                  onHover={setHoveredCard}
                />
              ))}
            </div>
          </div>

          {/* Response Style removed – always first person ('you') */}

          {/* Privacy */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Privacy</Label>
            <div className="grid grid-cols-3 gap-4">
              {privacyOptions.map((option) => {
                const isPrivateOrExclusive = option.value === "private" || option.value === "exclusive";
                const shouldDisable = isPrivateOrExclusive && !isPremium;
                
                return (
                  <OptionCard
                    key={option.value}
                    option={option}
                    isSelected={formData.privacy === option.value}
                    onSelect={handlePrivacySelect}
                    isHovered={hoveredCard === option.value}
                    onHover={setHoveredCard}
                    disabled={shouldDisable}
                    disabledReason={shouldDisable ? "Requires Posit Plus" : undefined}
                  />
                );
              })}
            </div>
          </div>

          {/* Monetization */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Monetization</Label>
            <div className="grid grid-cols-2 gap-4">
              {monetizationOptions.map((option) => (
                <OptionCard
                  key={option.value}
                  option={option}
                  isSelected={formData.monetization === option.value}
                  onSelect={(value) => handleInputChange("monetization", value)}
                  isHovered={hoveredCard === option.value}
                  onHover={setHoveredCard}
                />
              ))}
            </div>
            
            {formData.monetization === "paid" && (
              <div className="space-y-2 mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Label htmlFor="price" className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Monthly subscription price (required for paid posits)
                </Label>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">$</span>
                  <Input
                    id="price"
                    type="number"
                    min="1"
                    max="999"
                    value={formData.price}
                    onChange={(e) => handleInputChange("price", parseInt(e.target.value) || 0)}
                    className="w-32"
                    placeholder="5"
                  />
                  <span className="text-sm text-muted-foreground">per month</span>
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Revenue split: 90% you, 10% platform
                </p>
              </div>
            )}
          </div>

          <Button 
            type="submit" 
            size="lg" 
            className="w-full md:w-auto"
            disabled={
              isSubmitting || 
              !formData.positName.trim() || 
              !formData.audience || 
              !formData.responseType ||
              !formData.privacy || 
              !formData.monetization ||
              (formData.monetization === "paid" && (!formData.price || formData.price < 1))
            }
          >
            {isSubmitting ? "Creating Posit..." : "Create Posit"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default OnboardingFlow;
